import React, { useCallback, useRef, useState } from 'react';
import PendencyGroupTable from './PendencyGroupTable';
import { WarningOutlined } from '@ant-design/icons';
import { Button, Space, Table, Tooltip } from 'antd';
import stc from 'string-to-color';
import _ from 'lodash';
import { getGroupDragIds } from './planningAssign';

function isValidForBay(group, filters) {
    return (filters || []).every(({ filter }) =>
        (filter || []).every(filterItem => {
            if (!!filterItem?.key && !!filterItem?.path) {
                const { key, path, value } = filterItem;
                return value?.includes(group[`${path}.${key}`]);
            }
            return true;
        })
    );
}

function DataPivotView({ assigned, pivotkeys }) {
    if (!assigned?.length) return null;

    const keys = pivotkeys || [];
    const mergedBySubgroup = {};
    assigned.forEach(({ data, group }) => {
        const subGroup = _.pick(group, keys);
        const key = JSON.stringify(subGroup);
        if (mergedBySubgroup[key]) {
            mergedBySubgroup[key].push(...data);
        } else {
            mergedBySubgroup[key] = [...data];
        }
    });

    const rows = Object.keys(mergedBySubgroup).map(key => ({
        ...JSON.parse(key),
        count: mergedBySubgroup[key]?.length,
    }));
    const cols = keys.map(key => ({ title: key, dataIndex: key }));

    return (
        <div
            style={{
                width: '100%',
                overflow: 'auto',
                border: '1px solid black',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#999',
                padding: '5px',
                margin: '5px',
                boxShadow: 'inset 3px 3px 5px #555',
            }}
        >
            <h3 style={{ color: 'white' }}>Score Card</h3>
            <Table
                style={{ width: '80%' }}
                dataSource={rows}
                columns={[
                    ...cols,
                    {
                        title: 'count',
                        render: (_, record) => record?.count,
                    },
                ]}
                pagination={false}
            />
        </div>
    );
}

function BayView({ bay, HandleDelete, onClearAll, pivotkeys, setSkuModal, onDropGroup }) {
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const dragDepth = useRef(0);

    const assignedCount = bay?.assigned?.reduce((total, current) => total + current?.data?.length, 0);

    const handleDragEnter = useCallback(event => {
        event.preventDefault();
        dragDepth.current += 1;
        setIsDraggingOver(true);
    }, []);

    const handleDragOver = useCallback(event => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const handleDragLeave = useCallback(event => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        dragDepth.current = 0;
        setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback(
        event => {
            event.preventDefault();
            dragDepth.current = 0;
            setIsDraggingOver(false);
            const groupIds = getGroupDragIds(event);
            if (groupIds.length) onDropGroup(groupIds, bay.id);
        },
        [bay.id, onDropGroup]
    );

    return (
        <div
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
                minHeight: '160px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: isDraggingOver ? 'gray' : 'whitesmoke',
                alignItems: 'center',
                justifyContent: 'flex-start',
                border: '1px solid black',
                marginBottom: '15px',
                boxShadow: 'inset 3px 3px 5px #555',
                padding: '5px',
            }}
        >
            <div
                style={{
                    backgroundColor: stc(bay?.name),
                    width: '100%',
                    textAlign: 'center',
                    borderRadius: '13px 13px 0 0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: '-2px -2px 5px #555',
                }}
            >
                <Space>
                    {assignedCount > bay?.capacity && (
                        <Tooltip title="Capacity for Bay Reached">
                            <WarningOutlined style={{ color: 'red' }} />
                        </Tooltip>
                    )}
                    <h2 style={{ fontSize: '1rem', color: 'white' }}>{`${bay.name} (${assignedCount}/${bay?.capacity})`}</h2>
                    {bay?.assigned?.length > 0 && (
                        <Button
                            size="small"
                            onClick={event => {
                                event.stopPropagation();
                                onClearAll(bay.id);
                            }}
                        >
                            Clear all
                        </Button>
                    )}
                </Space>
            </div>
            <div style={{ width: '100%', padding: '8px 0' }}>
                {bay?.assigned?.length > 0 ? (
                    <PendencyGroupTable
                        data={bay.assigned}
                        draggable={false}
                        scrollY={null}
                        setSkuModal={setSkuModal}
                        onDelete={record => HandleDelete(bay?.id, record.id)}
                        isRowValid={record => isValidForBay(record.group, bay?.filters)}
                    />
                ) : (
                    <div className="pendency-bay-empty">Drop groups here</div>
                )}
            </div>
            <DataPivotView assigned={bay?.assigned} pivotkeys={pivotkeys} />
        </div>
    );
}

export default React.memo(BayView);
